import WebPhoneSDK from "ringcentral-web-phone";
import { settings } from "../Settings";
import { ringCentralCallStatus } from "../api/apiManager";
import { CallQualityMonitor } from "./callQualityMonitor";

const normalizeDialString = (value) => {
  if (!value) return "";
  return String(value)
    .trim()
    .replace(/[^\d*#+]/g, "")
    .replace(/^\+/, "");
};

export const CALL_STATUS = {
  IDLE: "Idle",
  CALLING: "Calling...",
  TRYING: "Trying...",
  RINGING: "Ringing...",
  CONNECTED: "Connected",
  IVR: "IVR Menu",
  HOLD: "On Hold",
  DISCONNECTED: "Call Ended",
  FAILED: "Failed",
  BUSY: "Busy",
  NO_ANSWER: "No Answer",
  DECLINED: "Call Declined",
};

// Statuses after which no more media flows for the current call.
const TERMINAL_STATUSES = new Set([
  CALL_STATUS.DISCONNECTED,
  CALL_STATUS.FAILED,
  CALL_STATUS.BUSY,
  CALL_STATUS.NO_ANSWER,
  CALL_STATUS.DECLINED,
]);

class RingCentralWebPhoneService {
  constructor() {
    this.webPhone = null;
    this.currentSession = null;
    this.isReady = false;
    this.isConnecting = false;
    this.lastStatus = CALL_STATUS.IDLE;
    this.lastError = "";
    this.listeners = new Set();
    this._silenceInterval = null;
    this._stateInterval = null;
    this._disposeInterval = null;
    this._connectedTimer = null;
    this._dialing = false;
    this._outboundHandler = null;
    // Per-call sipClient "inboundMessage" listener; tracked so it can be removed
    // on the next call / reset instead of leaking across calls.
    this._sipInboundHandler = null;
    this._telephonyInterval = null;
    // Webhook-driven status (primary) vs polling (dormant fallback). When a
    // ReceiveCallStatus webhook lands we cancel the fallback so we never poll.
    this._webhookGraceTimer = null;
    this._webhookSeen = false;
    this._sawWebhookLive = false;
    this._activeTelephonySessionId = null;
    this._dialedNumber = null;
    // Media quality sampler for the live call (see callQualityMonitor.js).
    // Started on CONNECTED, sampling stopped with the other timers, summary
    // emitted as a "quality" event once the terminal status is known.
    this._quality = null;
    // How the CURRENT call ended, first cause wins (see _markEnd):
    //   agent          our hangup() ran (user pressed End)
    //   remote         RC/SDK ended the session and we never hung up
    //   socket-dropped the SIP WebSocket closed under a live call
    //   page-closed    tab closed / refreshed mid-call
    // At flush time "remote" is re-read against the quality monitor: if the
    // audio path had already failed (ICE disconnected, mic track gone, outbound
    // stalled) it becomes "dropped-media" / "mic-lost". Reported in the quality
    // row's RequestBody so a daily "who dropped" report is a plain GROUP BY.
    this._endedBy = null;
    this._endDetail = null;
    this._wsCloseTarget = null;
    this._wsCloseHandler = null;
    // App user id of the current registration — used to tell the backend WHICH
    // pooled extension to query for call status (active-calls is extension-scoped).
    this._appUserId = null;
    // Inbound calls: the pending (not-yet-answered) incoming session and its
    // listener/cancel watcher.
    this.incomingSession = null;
    this._inboundHandler = null;
    this._incomingDisposeInterval = null;
  }

  // =====================
  // STATE
  // =====================
  getState() {
    return {
      isReady: this.isReady,
      isConnecting: this.isConnecting,
      lastError: this.lastError,
      currentSession: this.currentSession,
      status: this.lastStatus,
    };
  }

  getCurrentSession() {
    return this.currentSession;
  }

  // True while a call is live, ringing, or mid-dial. Used to avoid tearing down
  // / re-registering the softphone during an active call.
  isOnCall() {
    return !!(this.currentSession || this.incomingSession || this._dialing);
  }

  // Stricter than isOnCall(): true only if there is a session that is NOT in a
  // terminal state (or we're mid-dial). isOnCall() stays true for a session that
  // ended but was never nulled — the pool-heartbeat safety net keys off this so
  // it can self-stop even if a novel terminal state slips past the dispose
  // watcher.
  hasLiveSession() {
    if (this._dialing) return true;
    if (this.currentSession && !this._isDeadState(this.currentSession)) return true;
    if (this.incomingSession && !this._isDeadState(this.incomingSession)) return true;
    return false;
  }

  // Best-effort check of the underlying SIP WebSocket. Returns true (alive),
  // false (definitely closed/closing), or null (unknown SDK shape -> don't act).
  // Used by the keep-alive so it only re-registers when the link is really down.
  isConnectionAlive() {
    try {
      const ws =
        this.webPhone?.sipClient?.wsc ||
        this.webPhone?.sipClient?.ws ||
        this.webPhone?.sipClient?.webSocket ||
        this.webPhone?.client?.wsc;
      if (!ws || typeof ws.readyState !== "number") return null;
      return ws.readyState === 1; // WebSocket.OPEN
    } catch (e) {
      return null;
    }
  }

  // Wait for the SIP WebSocket to reach OPEN before dialing. webPhone.start() can
  // resolve while the socket is still CONNECTING (readyState 0) on slow/throttled
  // networks, and call() then throws InvalidStateError ("Failed to execute 'send'
  // on 'WebSocket': Still in CONNECTING state"). isConnectionAlive() returns false
  // while CONNECTING and true once OPEN. Returns:
  //   true  -> socket is OPEN (or its state can't be introspected -> don't block)
  //   false -> still not open after the timeout (network/proxy likely blocking :8083)
  async waitForConnectionOpen(timeoutMs = 6000) {
    const started = Date.now ? Date.now() : new Date().getTime();
    const now = () => (Date.now ? Date.now() : new Date().getTime());
    while (now() - started < timeoutMs) {
      const alive = this.isConnectionAlive();
      if (alive === true) return true; // OPEN
      if (alive === null) return true; // unknown SDK shape -> can't wait meaningfully
      // alive === false -> CONNECTING/closed; wait and re-check.
      await new Promise((r) => setTimeout(r, 200));
    }
    // Timed out. Only report failure if the socket is DEFINITIVELY not open.
    return this.isConnectionAlive() !== false;
  }

  _isDeadState(session) {
    const st = session?.state;
    return (
      !session ||
      st === "disposed" ||
      st === "failed" ||
      st === "terminated"
    );
  }

  // A previous call's session reference can linger if its dispose poll never
  // saw a terminal state. Such a stale reference would make the inbound guard
  // wrongly decline the NEXT incoming call (it "looks busy"). Clear references
  // that are actually dead so a genuinely-idle phone accepts new calls.
  _clearDeadSessions() {
    if (this.currentSession && this._isDeadState(this.currentSession)) {
      this._stopAllTimers();
      this.currentSession = null;
    }
    if (this.incomingSession && this._isDeadState(this.incomingSession)) {
      if (this._incomingDisposeInterval) {
        clearInterval(this._incomingDisposeInterval);
        this._incomingDisposeInterval = null;
      }
      this.incomingSession = null;
    }
  }

  // =====================
  // EVENTS
  // =====================
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  emit(payload) {
    this.listeners.forEach((fn) => fn(payload));
  }

  setStatus(status) {
    if (this.lastStatus === status) return;
    this.lastStatus = status;
    if (status === CALL_STATUS.CONNECTED) {
      this._startQualityMonitor();
    } else if (TERMINAL_STATUSES.has(status)) {
      // Emit the quality summary BEFORE the terminal status: the context's
      // status handler runs cleanupSingleCallState(), which clears the dialed
      // number / direction refs the quality row is tagged with.
      this._flushQualitySummary(status);
    }
    this.emit({ type: "status", status });
  }

  // =====================
  // CALL QUALITY
  // =====================
  // Grab the live session's RTCPeerConnection at CONNECTED time and sample it.
  // Idempotent across HOLD -> CONNECTED (same call keeps its monitor).
  _startQualityMonitor() {
    try {
      const pc = this.currentSession?.rtcPeerConnection;
      if (!pc) return;
      if (this._quality && this._quality.pc === pc) return;
      // A previous call's summary that never got flushed (novel terminal path)
      // is emitted now rather than dropped.
      this._flushQualitySummary("superseded");
      this._endedBy = null;
      this._endDetail = null;
      this._quality = new CallQualityMonitor(pc, {
        callee: this._dialedNumber || null,
        telephonySessionId: this._activeTelephonySessionId || null,
      });
      this._quality.start();
      this._watchSipSocketForDrop();
    } catch (e) {
      /* quality sampling must never affect the call */
    }
  }

  // Record the first known cause of the current call's end. Later causes are
  // consequences (our hangup() -> SDK terminates -> dispose watcher fires), so
  // only the first one is kept.
  _markEnd(cause, detail) {
    if (this._endedBy) return;
    this._endedBy = cause;
    this._endDetail = detail || null;
  }

  // The SIP WebSocket closing while a call is live is the "our leg went Gone"
  // case RingCentral reports. No BYE reaches the browser, so without this the
  // end would arrive later via webhook and be misread as the far end hanging up.
  _watchSipSocketForDrop() {
    this._unwatchSipSocket();
    try {
      const ws =
        this.webPhone?.sipClient?.wsc ||
        this.webPhone?.sipClient?.ws ||
        this.webPhone?.sipClient?.webSocket ||
        this.webPhone?.client?.wsc;
      if (!ws || typeof ws.addEventListener !== "function") return;
      const handler = (ev) => {
        if (this.hasLiveSession()) {
          this._markEnd("socket-dropped", `ws close code=${ev?.code ?? "?"}`);
          console.warn("[RC] SIP socket closed under a live call", ev?.code);
        }
      };
      ws.addEventListener("close", handler);
      this._wsCloseTarget = ws;
      this._wsCloseHandler = handler;
    } catch (e) {
      /* ignore */
    }
  }

  _unwatchSipSocket() {
    try {
      if (this._wsCloseTarget && this._wsCloseHandler) {
        this._wsCloseTarget.removeEventListener("close", this._wsCloseHandler);
      }
    } catch (e) {
      /* ignore */
    }
    this._wsCloseTarget = null;
    this._wsCloseHandler = null;
  }

  // Final "ended by" for the quality row. Combines the first-cause marker with
  // the monitor's media-failure evidence.
  _resolveEndedBy(q) {
    let endedBy = this._endedBy || "unknown";
    let detail = this._endDetail;
    let mf = { failed: false, reason: null };
    try {
      mf = q?.mediaFailureBeforeEnd?.() || mf;
    } catch (e) {
      /* ignore */
    }
    if (endedBy === "remote" || endedBy === "unknown") {
      if (q?.micLostAt != null) {
        endedBy = "mic-lost";
        detail = `mic track ended at ${q.micLostAt}s`;
      } else if (mf.failed) {
        endedBy = "dropped-media";
        detail = mf.reason;
      } else if (this.isConnectionAlive() === false) {
        endedBy = "socket-dropped";
        detail = detail || "SIP socket not open at call end";
      }
    } else if (endedBy === "agent" && mf.failed) {
      // The user pressed End, but the audio had already died — they hung up
      // on a dead call. Keep "agent" but say so.
      detail = `after media loss: ${mf.reason}`;
    }
    return { endedBy, endDetail: detail };
  }

  // Tab closed / refreshed mid-call. Returns the summary for the caller to
  // send with a beacon (the normal event path is gone once the page unloads).
  flushForUnload() {
    const q = this._quality;
    if (!q) return null;
    this._markEnd("page-closed");
    this._quality = null;
    this._unwatchSipSocket();
    try {
      q.stopSampling();
      const summary = q.summary("page-closed");
      if (!summary) return null;
      const { endedBy, endDetail } = this._resolveEndedBy(q);
      summary.endedBy = endedBy;
      summary.endDetail = endDetail;
      summary.line = `${endedBy} | ${summary.line}`;
      return summary;
    } catch (e) {
      return null;
    }
  }

  _stopQualitySampling() {
    try {
      this._quality?.stopSampling();
    } catch (e) {
      /* ignore */
    }
  }

  _flushQualitySummary(endStatus) {
    const q = this._quality;
    if (!q) return;
    this._quality = null;
    this._unwatchSipSocket();
    try {
      q.stopSampling();
      const summary = q.summary(endStatus);
      if (summary) {
        const { endedBy, endDetail } = this._resolveEndedBy(q);
        summary.endedBy = endedBy;
        summary.endDetail = endDetail;
        summary.line = `${endedBy} | ${summary.line}`;
        this.emit({ type: "quality", summary });
      }
    } catch (e) {
      /* ignore */
    }
  }

  // =====================
  // SDK
  // =====================
  getSdk() {
    // The SDK is bundled via npm (import at the top) so it can never be missing
    // at runtime. It used to load from a jsdelivr <script> tag — when a user's
    // network blocked the CDN at page load, window.WebPhone stayed undefined
    // and every dial failed with "WebPhone SDK not found" until a refresh.
    // window.WebPhone is kept only as a fallback for a stale cached index.html
    // that still carries the CDN tag.
    const SDK = WebPhoneSDK || window?.WebPhone;
    if (!SDK) throw new Error("WebPhone SDK not found");
    return SDK;
  }

  // =====================
  // AUDIO
  // =====================
  _startSilenceInterval() {
    // Intentionally a no-op. This used to force-mute every <audio> element once
    // per second to suppress the SDK's ringback tone — but ringtones are already
    // disabled (audioHelper.enabled = false), so the loop served no purpose and
    // risked periodically muting the live call audio (causing voice to drop in
    // and out). Kept as a no-op so existing call sites don't break.
    this._stopSilenceInterval();
  }

  _stopSilenceInterval() {
    if (this._silenceInterval) {
      clearInterval(this._silenceInterval);
      this._silenceInterval = null;
    }
  }

  // Build a DeviceManager for the WebPhone SDK. The SDK's built-in
  // DefaultDeviceManager.getInputDeviceId() returns the FIRST audioinput's
  // deviceId unconditionally, and the SDK then opens the mic with
  // getUserMedia({ audio: { deviceId: { exact: <id> } } }). On some machines
  // (certain privacy/enterprise configs) enumerateDevices() returns the first
  // audioinput with an EMPTY deviceId right after the SDK stops its permission-
  // probe stream — so the SDK requests deviceId:{exact:""} and Chrome throws
  // "Requested device not found" -> "Call failed". (Twilio never hits this: it
  // just asks for { audio: true }.) This manager always yields a VALID id:
  // prefer a real enumerated device (or the "default" alias, which Chrome always
  // accepts), and if ids come back hidden/empty, briefly open a stream so
  // enumerateDevices() returns real ids before choosing.
  _createDeviceManager() {
    const pickDeviceId = async (kind) => {
      const enumerate = async () =>
        (await navigator.mediaDevices.enumerateDevices()).filter(
          (d) => d.kind === kind && d.deviceId,
        );
      let matches = await enumerate();
      if (matches.length === 0) {
        // Ids are hidden (no active capture / privacy config). Open a short-lived
        // stream to reveal real deviceIds, then release it.
        try {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          matches = await enumerate();
          s.getTracks().forEach((t) => t.stop());
        } catch {
          /* no mic / blocked — fall through to the default alias below */
        }
      }
      const preferred =
        matches.find((d) => d.deviceId === "default") || matches[0];
      return preferred?.deviceId;
    };
    return {
      // Never return "" (the SDK wraps it in { exact }, which fails). Fall back
      // to the browser "default" input alias so a call can't fail on device
      // selection when a real mic exists.
      getInputDeviceId: async () => {
        const id = await pickDeviceId("audioinput");
        if (id) return id;
        // No usable id. "default" is only safe when a mic actually exists (ids
        // hidden by a privacy config); with NO audioinput device Chrome rejects
        // { exact: "default" } mid-dial as the bare "Requested device not
        // found". Distinguish the two so a missing mic fails fast with an
        // actionable message instead.
        let anyMic = true;
        try {
          anyMic = (await navigator.mediaDevices.enumerateDevices()).some(
            (d) => d.kind === "audioinput",
          );
        } catch {
          /* can't tell — let the "default" alias try */
        }
        if (!anyMic) {
          const err = new Error(
            "No microphone found. Please connect a microphone (and check Windows Sound settings), then try again.",
          );
          err.name = "NotFoundError";
          throw err;
        }
        return "default";
      },
      // Output is optional; return undefined when unknown so the SDK skips
      // setSinkId (Firefox doesn't support output selection anyway).
      getOutputDeviceId: async () => await pickDeviceId("audiooutput"),
    };
  }

  // =====================
  // INIT
  // =====================
  async init(data = {}) {
    // Idempotent: if a softphone is already registered, reuse it. Creating a
    // second instance opens another SIP registration, which makes RingCentral
    // fork the outbound call so the recipient receives multiple simultaneous legs.
    if (this.isReady && this.webPhone) {
      return true;
    }

    try {
      this.isConnecting = true;

      const SDK = this.getSdk();
      const sipInfo = data?.sipInfo;

      if (!sipInfo) throw new Error("SIP info missing");

      // Remember the last init payload so a recovery re-register can fall back
      // to it if a fresh sip-info fetch isn't available.
      this._lastInitData = data;
      this._appUserId = data?.appUserId || this._appUserId;
      // RC INTERNAL extension id of this registration (from the allocate
      // payload's rcExtensionId; the pool's short extension NUMBER is useless
      // here). Second correlation key for webhook call-status events, so our
      // own events still match when their `to` couldn't be resolved.
      this._rcExtensionId = data?.rcExtensionId ?? null;

      const sdkOptions = {
        sipInfo,
        // Stable per-user SIP instance id so a refresh re-registers the SAME device
        // instead of adding another registration (avoids duplicate call legs).
        instanceId: `maxchats-${data?.appUserId || "web"}`,
        clientId: data?.clientId,
        // Custom device manager: the SDK default can request an empty deviceId
        // and fail with "Requested device not found" on some machines (see
        // _createDeviceManager). Ours always yields a valid mic id.
        deviceManager: this._createDeviceManager(),
        debug: false,
        audioHelper: {
          enabled: false,
          incoming: null,
          outgoing: null,
        },
        enableQos: false,
        enableMusicOnHold: false,
      };

      const createAndStart = async () => {
        this.webPhone = new SDK(sdkOptions);
        await this.webPhone.start();
      };

      // The SIP WebSocket (wss://...:8083) can fail its FIRST connect with a
      // bare WebSocket error Event — no .message, just type:"error" — on a
      // transient proxy/network hiccup, even when an immediate retry succeeds.
      // Those were the scattered one-off "event:error" register-network rows in
      // CallErrorLogs. Retry the create+start ONCE for that specific failure
      // shape before surfacing it. Real failures (bad SIP info, blocked port)
      // either carry a message or fail the retry too.
      try {
        await createAndStart();
      } catch (err) {
        const bareWsEvent =
          !err?.message &&
          (err?.type === "error" ||
            (typeof Event !== "undefined" && err instanceof Event));
        if (!bareWsEvent) throw err;
        try {
          await this.webPhone?.dispose?.();
        } catch (e) {
          /* first instance may already be dead */
        }
        this.webPhone = null;
        await new Promise((r) => setTimeout(r, 800));
        await createAndStart();
      }

      // Register the incoming-call listener now that the softphone is up, so
      // calls to the RingCentral number ring inside the app.
      this._attachInboundHandler();

      this.isReady = true;
      this.setStatus(CALL_STATUS.IDLE);

      return true;
    } catch (err) {
      this.lastError = err?.message;
      this.isReady = false;
      throw err;
    } finally {
      this.isConnecting = false;
    }
  }

  // Tear down the current softphone so a fresh init() can cleanly re-register.
  // The stable instanceId means re-registering REPLACES this device's SIP
  // registration rather than adding a duplicate leg. Callers MUST guard on
  // isOnCall() — never reset during a live/ringing/dialing call.
  async reset() {
    try {
      if (this._inboundHandler && this.webPhone?.off) {
        this.webPhone.off("inboundCall", this._inboundHandler);
      }
      if (this._outboundHandler && this.webPhone?.off) {
        this.webPhone.off("outboundCall", this._outboundHandler);
      }
      if (this._sipInboundHandler && this.webPhone?.sipClient?.off) {
        this.webPhone.sipClient.off("inboundMessage", this._sipInboundHandler);
      }
      await this.webPhone?.dispose?.();
    } catch (e) {
      /* ignore — phone may already be gone */
    }
    this._stopAllTimers();
    // A call torn down via reset() (logout / re-register) still gets its row.
    this._flushQualitySummary("reset");
    this._unwatchSipSocket();
    this.webPhone = null;
    this.isReady = false;
    this._inboundHandler = null;
    this._outboundHandler = null;
    this._sipInboundHandler = null;
    // Intentionally leave currentSession/incomingSession alone; isOnCall()
    // guards prevent reset() from being called while one exists.
  }

  // =====================
  // CALL
  // =====================

  async call(to, callerId) {
    // Self-heal an un-ready phone. The softphone can be left not-ready between
    // "registered" and "dial" — a stale terminal event / keep-alive / rapid
    // retry can run cleanupSingleCallState -> reset() (webPhone=null,
    // isReady=false) after initializeRingCentralOutbound already registered.
    // Throwing here produced the dial-stage "WebPhone not ready" and, if a caller
    // reached the handler wiring, "Cannot read properties of null (reading 'on')".
    // Instead, re-register from the last init payload and continue. (init() would
    // have thrown BEFORE this if the network genuinely can't register, so this
    // only recovers the reset-mid-dial race, not a blocked connection.)
    if (
      (!this.isReady || !this.webPhone) &&
      this._lastInitData &&
      !this._reinitInFlight
    ) {
      try {
        this._reinitInFlight = true;
        console.warn("[RC] call(): phone not ready — re-registering before dial");
        await this.reset();
        await this.init(this._lastInitData);
        // Let the fresh socket reach OPEN before we send (avoids InvalidStateError
        // "Still in CONNECTING state").
        await this.waitForConnectionOpen?.(6000);
      } catch (e) {
        /* fall through to the guard below */
      } finally {
        this._reinitInFlight = false;
      }
    }
    if (!this.isReady || !this.webPhone) throw new Error("WebPhone not ready");

    // A previous call's session reference can linger in a terminal state if its
    // dispose watcher / end path never nulled it (more likely now that call-end
    // arrives via webhook rather than the local dispose watcher). Left in place,
    // the guard below treats that dead session as "in progress" and returns
    // WITHOUT dialing — the screen shows "Calling..." but no INVITE goes out,
    // which the user sees as the dial button "doing nothing". Clear dead refs
    // first so a genuinely idle phone always places the new call.
    this._clearDeadSessions();

    // Guard against overlapping dials (double-click, re-render, or multiple code
    // paths calling this). Without it the recipient gets several simultaneous legs.
    // Only a LIVE session (or an in-flight dial) should block — a dead reference
    // was just cleared above.
    if (
      (this.currentSession && !this._isDeadState(this.currentSession)) ||
      this._dialing
    ) {
      console.warn("[RC] Call already in progress; ignoring duplicate dial");
      return this.currentSession;
    }
    this._dialing = true;

    // Remember the dialed number so the telephony-status poller can match it
    // against RingCentral's active-call list.
    this._dialedNumber = to;

    const number = normalizeDialString(to);
    this.setStatus(CALL_STATUS.CALLING);
    this._startSilenceInterval();

    // Register the outbound handler once; remove any previous one first so handlers
    // don't accumulate across calls on the persistent WebPhone instance.
    if (this._outboundHandler) {
      this.webPhone.off?.("outboundCall", this._outboundHandler);
    }
    this._outboundHandler = (session) => {
      this.currentSession = session;
      window.__rcSession = session;
      this._pollSessionState(session);
    };
    // Null-safe: webPhone is guaranteed non-null past the guard above, but attach
    // defensively so a torn-down phone can never throw "reading 'on'" here.
    this.webPhone?.on?.("outboundCall", this._outboundHandler);

    // sipClient messages monitor karo - reject detect karne ke liye.
    // Parse the SIP status line so ANY final failure response (4xx/5xx/6xx)
    // surfaces. The old code only caught 487/486/603/480, so a 403 (caller ID
    // not allowed), 404 (bad number), 408 (timeout) or 488/5xx left the UI stuck
    // on "Calling..." forever.
    const sipClient = this.webPhone?.sipClient;
    if (sipClient) {
      const handleInbound = (message) => {
        const subject = message?.subject || "";

        // Status line looks like "SIP/2.0 403 Forbidden". Pull the response code.
        const match = subject.match(/SIP\/2\.0\s+(\d{3})/);
        const code = match ? parseInt(match[1], 10) : null;

        // 180 Ringing / 183 Session Progress -> remote end is alerting.
        if (code === 180 || code === 183) {
          this.setStatus(CALL_STATUS.TRYING);
          return;
        }

        // 401 Unauthorized / 407 Proxy Auth Required are the normal SIP digest
        // challenges — the SDK transparently re-sends the INVITE with credentials
        // and the call proceeds. They are NOT call failures, so ignore them (a
        // previous version treated any >=400 as failure, which tore down the
        // caller's screen on the auth challenge while the callee still rang).
        if (code === 401 || code === 407) {
          return;
        }

        // 481 (Call/Transaction Does Not Exist — "Dialog has gone") and 487
        // (Request Terminated) are normal END-OF-CALL artifacts, NOT dial
        // failures: they come back when a BYE/CANCEL races an already-gone
        // dialog — e.g. the answer-poller ends the call AND the user presses
        // End, or the callee hung up first. Treating them as FAILED logged a
        // scary "[RC SIP FAIL]" and could clobber the call's real end state, so
        // just ignore them (the telephony poller / dispose watcher handles the
        // real teardown).
        if (code === 481 || code === 487) {
          console.debug(`[RC SIP] benign teardown ${code}`, subject);
          return;
        }

        // 1xx provisional (100/180/183) and 2xx success are NOT failures.
        if (code !== null && code >= 400) {
          let status = CALL_STATUS.DISCONNECTED;
          if (code === 486 || code === 600 || code === 603)
            status = CALL_STATUS.BUSY;
          else if (code === 480 || code === 408)
            status = CALL_STATUS.NO_ANSWER;
          else status = CALL_STATUS.FAILED;

          console.warn(`[RC SIP FAIL] INVITE rejected with ${code}`, subject);
          this.lastError = `SIP ${code}: ${subject}`;
          this._stopAllTimers();
          this._stopSilenceInterval();
          this.currentSession = null;
          this.setStatus(status);
        }
      };
      // Remove any handler left over from a previous call on this persistent
      // sipClient so listeners don't accumulate across calls (they used to be
      // removed only when a >=400 arrived, leaking one per successful call).
      if (this._sipInboundHandler) {
        sipClient.off?.("inboundMessage", this._sipInboundHandler);
      }
      this._sipInboundHandler = handleInbound;
      sipClient.on("inboundMessage", handleInbound);
    }

    try {
      let session;
      try {
        session = await this.webPhone.call(number, callerId);
      } catch (err) {
        // The SDK sends the INVITE on the SIP WebSocket inside call(). If that
        // socket is still CONNECTING it throws InvalidStateError ("Failed to
        // execute 'send' on 'WebSocket': Still in CONNECTING state"). Our pre-dial
        // waitForConnectionOpen() covers this when the socket is introspectable,
        // but if the SDK hides the socket (isConnectionAlive() === null) the wait
        // can't help. So catch that exact error, wait for the socket to open, and
        // retry the send ONCE. Any other error (or a second failure) propagates.
        const msg = `${err?.name || ""} ${err?.message || ""}`;
        const stillConnecting =
          /InvalidStateError/i.test(msg) ||
          (/websocket/i.test(msg) && /connecting/i.test(msg));
        if (!stillConnecting) throw err;
        console.warn(
          "[RC] INVITE hit a still-CONNECTING socket — waiting for open, retrying once",
        );
        await this.waitForConnectionOpen?.(6000);
        await new Promise((r) => setTimeout(r, 300));
        session = await this.webPhone.call(number, callerId);
      }
      // Ensure the session is tracked even if the outboundCall event didn't fire.
      if (session && !this.currentSession) {
        this.currentSession = session;
        window.__rcSession = session;
        this._pollSessionState(session);
      }
      console.log("[RC CALL RETURNED]", session);
      return session;
    } catch (err) {
      this._stopSilenceInterval();
      this.setStatus(CALL_STATUS.FAILED);
      throw err;
    } finally {
      this._dialing = false;
    }
  }

  // =====================
  // SESSION STATE
  // =====================

  _pollSessionState(session) {
    if (!session) return;
    this._stopAllTimers();
    this.setStatus(CALL_STATUS.CALLING);

    session.on("ringing", () => {
      this.setStatus(CALL_STATUS.TRYING);
    });

    // RingCentral answers the browser's WebRTC leg immediately to deliver the
    // ringback tone, so the SDK "answered" event fires while the PSTN party is
    // still ringing — it is NOT a real pickup. Treat it as "Ringing...", not
    // Connected. The true pickup is detected via RingCentral's telephony status
    // (see _startTelephonyStatusPolling), which flips the status to CONNECTED.
    session.on("answered", () => {
      if (this.lastStatus !== CALL_STATUS.CONNECTED) {
        this.setStatus(CALL_STATUS.TRYING);
      }
    });

    //  Dispose check. Use _isDeadState so this matches the inbound watchers and
    //  the shared helper — it must include "terminated" (a valid SDK terminal
    //  state), not just "disposed"/"failed". Missing "terminated" here meant an
    //  answered call that ended in "terminated" never emitted DISCONNECTED, so
    //  the pool heartbeat kept beating and the extension slot leaked for the life
    //  of the tab.
    const disposeCheck = setInterval(() => {
      if (this._isDeadState(session)) {
        console.warn("[RC] call ended by dispose watcher; session.state=", session?.state);
        this._markEnd("remote", `sip session ${session?.state}`);
        clearInterval(disposeCheck);
        this._disposeInterval = null;
        this._stopAllTimers();
        this.currentSession = null;
        this._stopSilenceInterval();
        this.setStatus(CALL_STATUS.DISCONNECTED);
        setTimeout(() => {
          this._pollCallLog();
        }, 3000);
      }
    }, 300);

    this._disposeInterval = disposeCheck;

    // Track the real PSTN pickup (Ringing -> CallConnected), since the SIP leg
    // can't tell. PRIMARY signal is the RingCentral webhook (ReceiveCallStatus
    // over SignalR); polling active-calls is only a dormant fallback now.
    this._startStatusTracking();
  }

  // Begin tracking call progress. Webhooks are primary; we wait a short grace
  // window for one and only fall back to polling if none arrives (e.g. local dev
  // with no public webhook URL, or a dropped subscription). In the normal
  // webhook-working case NOTHING polls active-calls, so no Heavy-group 429s.
  _startStatusTracking() {
    this._webhookSeen = false;
    this._sawWebhookLive = false;
    // Cleared per call so a terminal event is matched to THIS call's session only.
    this._activeTelephonySessionId = null;
    if (this._webhookGraceTimer) clearTimeout(this._webhookGraceTimer);

    const WEBHOOK_GRACE_MS = 5000;
    this._webhookGraceTimer = setTimeout(() => {
      this._webhookGraceTimer = null;
      const active =
        this.lastStatus === CALL_STATUS.CALLING ||
        this.lastStatus === CALL_STATUS.TRYING;
      if (!this._webhookSeen && active) {
        console.warn(
          "[RC] no webhook call-status within grace window — falling back to polling",
        );
        this._startTelephonyStatusPolling();
      }
    }, WEBHOOK_GRACE_MS);
  }

  // Apply a call-status update PUSHED from the backend RingCentral webhook (via
  // SignalR "ReceiveCallStatus"). This is what flips "Ringing..." -> "Connected"
  // and detects pre-pickup end reasons without polling. Mirrors the transitions
  // the poll drives, so behaviour is identical to the old poll-only path.
  applyRemoteTelephonyStatus(payload) {
    if (!payload) return;
    const status = payload.telephonyStatus;

    // Only meaningful for an active OUTBOUND call on this browser.
    const active =
      this.lastStatus === CALL_STATUS.CALLING ||
      this.lastStatus === CALL_STATUS.TRYING ||
      this.lastStatus === CALL_STATUS.CONNECTED;
    if (!active) return;

    // Events are broadcast to ALL clients and cover EVERY call on the RC
    // account (account-level subscription): every teammate's outbound calls,
    // all inbound calls, internal extension-to-extension calls. Correlate
    // STRICTLY to this browser's call — a foreign event must never drive our
    // state. Historically an event whose `to` couldn't be resolved (per-party
    // events often omit it; the backend's _sessionTo memory is per-process, so
    // it misses on a multi-instance host or after a restart) skipped the number
    // check entirely: a stranger's Ringing/CallConnected re-locked our session
    // id or "connected" our ringing call, and a stranger's terminal event —
    // decisive (their callee declined/busy/voicemail) or matching the wrongly
    // locked session — ENDED our live call. That was the team's "calls
    // auto-disconnect randomly" (worst with several agents calling at once).
    const tail = (s) => (s ? String(s).replace(/\D/g, "").slice(-10) : "");
    const evtSession = payload.telephonySessionId || null;
    const locked = this._activeTelephonySessionId;
    const toConflicts =
      !!payload.to &&
      !!this._dialedNumber &&
      tail(payload.to) !== tail(this._dialedNumber);
    const toMatches =
      !!payload.to &&
      !!this._dialedNumber &&
      tail(payload.to) === tail(this._dialedNumber);
    const extMatches =
      !!payload.extensionId &&
      !!this._rcExtensionId &&
      String(payload.extensionId) === String(this._rcExtensionId);

    // A different dialed number is definitely someone else's call — even if the
    // extension matches (a stale event for the PREVIOUS call on our pooled line).
    if (toConflicts) return;
    if (locked) {
      // Locked onto this call's telephony session: from here on only that
      // session may drive our state — a foreign event must not re-lock us,
      // prematurely "connect" us, or end us.
      if (evtSession !== locked) return;
    } else if (!toMatches && !extMatches) {
      // Not locked yet, and nothing positively ties this event to OUR call
      // (our own events always carry our extensionId, and usually our dialed
      // number) — it's someone else's call or unattributable; ignore it.
      return;
    }

    // OUR call's webhook landed — cancel the dormant poll fallback (and stop any
    // poll that may already have started) so we stay at zero polling.
    this._webhookSeen = true;
    if (this._webhookGraceTimer) {
      clearTimeout(this._webhookGraceTimer);
      this._webhookGraceTimer = null;
    }
    if (this._telephonyInterval) {
      clearTimeout(this._telephonyInterval);
      this._telephonyInterval = null;
    }

    if (status === "CallConnected") {
      this._sawWebhookLive = true;
      // Lock onto THIS call's telephony session so we can tell a terminal event
      // for our call apart from a stale one for a previous call to the same
      // number. Lock ONCE — never re-lock from a later event.
      if (!locked && evtSession) {
        this._activeTelephonySessionId = evtSession;
      }
      if (
        this.lastStatus === CALL_STATUS.CALLING ||
        this.lastStatus === CALL_STATUS.TRYING
      ) {
        this.setStatus(CALL_STATUS.CONNECTED);
      }
    } else if (status === "Ringing") {
      this._sawWebhookLive = true;
      if (!locked && evtSession) {
        this._activeTelephonySessionId = evtSession;
      }
      if (this.lastStatus === CALL_STATUS.CALLING) {
        this.setStatus(CALL_STATUS.TRYING);
      }
    } else if (status === "NoCall") {
      // Terminal without pickup (decline / busy / voicemail / callee hung up).
      // Session-mismatch filtering already happened in the gate above; here we
      // only decide whether a correlated terminal should end the call.
      // A DECISIVE reason (the callee actively declined / was busy / hit
      // voicemail) is a real outcome of THIS ring — a lingering previous call
      // shows up as Disconnected/Gone, not these. So honor a decisive reason even
      // if we only ever saw "Ringing" via SIP (which doesn't set _sawWebhookLive)
      // — otherwise a decline left the caller stuck on "Ringing…" forever (the
      // guard ignored the terminal AND this webhook cancelled the poll fallback).
      const r = String(payload.endReason || "").toLowerCase();
      const decisive = r === "rejected" || r === "busy" || r === "voicemail";
      if (
        this._sawWebhookLive ||
        this.lastStatus === CALL_STATUS.CONNECTED ||
        (locked && evtSession === locked) ||
        decisive
      ) {
        this._endFromPoll(null, payload.endReason);
      }
    }
    // "RateLimited"/"NotFound" don't occur on the webhook path.
  }

  // Poll the backend (which has the JWT access token) for the live telephony
  // status of this outbound call. RingCentral reports "Ringing" while the
  // callee's phone rings and "CallConnected" only when they truly pick up — so
  // this is what flips the UI from "Ringing..." to "Connected".
  _startTelephonyStatusPolling() {
    if (this._telephonyInterval) {
      clearTimeout(this._telephonyInterval);
      this._telephonyInterval = null;
    }

    // Track whether RingCentral has actually reported this call as live yet.
    let sawActiveCall = false;
    let noCallStreak = 0; // consecutive DEFINITIVE terminal ("NoCall") reads
    let notFoundStreak = 0; // consecutive ambiguous "NotFound" reads
    let stopped = false;

    // Base cadence. Was 1.5s, which (× concurrent pooled calls) hammered
    // RingCentral's active-calls endpoint past its low "Heavy"-group rate limit
    // and got a flood of 429s — and while rate-limited the "Ringing -> Connected"
    // flip couldn't fire. 3s keeps us under the limit; on a 429 we back off
    // further using the server's Retry-After. Thresholds are halved so end-of-
    // call detection stays about as fast as before (~6s live, ~12s backstop).
    const BASE_INTERVAL = 3000;

    const stop = () => {
      stopped = true;
      if (this._telephonyInterval) {
        clearTimeout(this._telephonyInterval);
        this._telephonyInterval = null;
      }
    };

    const poll = async () => {
      if (stopped) return;
      let nextDelay = BASE_INTERVAL;
      try {
        const res = await ringCentralCallStatus(this._dialedNumber, this._appUserId);
        const status = res?.data?.telephonyStatus;
        // Diagnostic: shows exactly what the poll sees each tick and the streaks
        // that drive end-of-call. Remove once the "rings then drops" is resolved.
        console.log(
          "[RC poll]",
          "status=", status,
          "partyStatus=", res?.data?.partyStatus,
          "noCallStreak=", noCallStreak,
          "notFoundStreak=", notFoundStreak,
          "uiStatus=", this.lastStatus,
        );

        if (status === "RateLimited") {
          // Backend was rate-limited by RingCentral. Back off for the advised
          // time (min the base cadence) and DON'T touch call state.
          const retryAfter = Number(res?.data?.retryAfter) || 5;
          nextDelay = Math.max(retryAfter * 1000, BASE_INTERVAL);
        } else if (status === "CallConnected") {
          // Real pickup. Flip to Connected (only from the ringing phase) and
          // stop polling — hold/end are handled elsewhere.
          sawActiveCall = true;
          noCallStreak = 0;
          if (
            this.lastStatus === CALL_STATUS.CALLING ||
            this.lastStatus === CALL_STATUS.TRYING
          ) {
            this.setStatus(CALL_STATUS.CONNECTED);
          }
          stop();
          return;
        } else if (status === "Ringing") {
          // Callee is being alerted but hasn't answered yet.
          sawActiveCall = true;
          noCallStreak = 0;
          notFoundStreak = 0;
          if (this.lastStatus === CALL_STATUS.CALLING) {
            this.setStatus(CALL_STATUS.TRYING);
          }
        } else if (status === "NoCall") {
          // Terminal party status from RC (Disconnected/Gone/VoiceMail/Rejected/
          // Busy/Parked). RC answers our browser leg for ringback so no SIP
          // 4xx/6xx arrives — this poll is the only pre-pickup "ended" signal.
          // Only a reliable "the CURRENT call ended" signal if we've actually
          // seen THIS call go live (Ringing/Connected) first. Before that, a
          // terminal read is almost always a STALE just-ended PREVIOUS call still
          // lingering in active-calls on this pooled extension (classic on
          // re-dial: "call again ends immediately") — killing the new call on it
          // is wrong. Seen-live -> real end after 2 reads; not-yet-live -> treat
          // as ambiguous like NotFound and wait for the new call to appear.
          // A DECISIVE reason (Rejected/Busy/VoiceMail) is a real outcome of THIS
          // ring — a stale lingering previous call reads as Disconnected/Gone, not
          // these — so end on it after 2 reads even if we never saw the call go
          // "live". Without this a fast decline left the caller stuck on "Ringing…"
          // until the 12-read (~36s) backstop, and then ended as a generic Failed
          // instead of "Call declined".
          const pr = String(res?.data?.endReason || "").toLowerCase();
          const decisive =
            pr === "rejected" || pr === "busy" || pr === "voicemail";
          if (sawActiveCall || decisive) {
            notFoundStreak = 0;
            noCallStreak += 1;
            if (noCallStreak >= 2) {
              this._endFromPoll(stop, res?.data?.endReason);
              return;
            }
          } else {
            noCallStreak = 0;
            notFoundStreak += 1;
            if (notFoundStreak >= 12) {
              this._endFromPoll(stop);
              return;
            }
          }
        } else if (status === "NotFound") {
          // AMBIGUOUS: the call isn't listed in active-calls. This happens for a
          // genuinely-ringing call that RC hasn't listed yet (eventual
          // consistency / rate-limiting) AND for a call that has ended. It is NOT
          // a reliable "ended" signal, so it must NOT kill a call that is still
          // ringing (this was the "rings a few seconds then 'Call could not be
          // connected'" bug). Real ends are caught by terminal "NoCall" above or
          // the SIP dispose watcher; only give up after a long fallback so an
          // orphaned session doesn't ring forever.
          noCallStreak = 0;
          notFoundStreak += 1;
          if (notFoundStreak >= 12) {
            this._endFromPoll(stop);
            return;
          }
        }
        // status falsy (no data) -> just keep polling at base cadence.
      } catch (e) {
        /* transient network error — keep polling */
      }

      if (!stopped) {
        this._telephonyInterval = setTimeout(poll, nextDelay);
      }
    };

    // Kick off immediately; subsequent polls self-schedule via setTimeout.
    this._telephonyInterval = setTimeout(poll, 0);
  }

  // End a call from the telephony poller. Ended AFTER pickup -> "Call Ended".
  // Ended BEFORE pickup -> map the RC end reason to an accurate status so a
  // decline/busy/no-answer isn't shown as a technical failure:
  //   Rejected -> Declined, Busy -> Busy, VoiceMail -> No Answer,
  //   Disconnected/Gone (callee hung up during ring) -> Call Ended,
  //   unknown/orphaned -> Failed.
  _endFromPoll(stop, reason) {
    console.warn("[RC] call ended by telephony poll (_endFromPoll); reason=", reason);
    // RingCentral reported the call over (webhook or poll) and we did not hang
    // up: the far end ended it, or RC's media server timed out on us — the
    // quality monitor tells those apart at flush time (_resolveEndedBy).
    this._markEnd("remote", `rc:${reason || "NoCall"}`);
    this._stopSilenceInterval();
    try {
      this.currentSession?.hangup?.();
    } catch {}
    this.currentSession = null;

    let status;
    if (this.lastStatus === CALL_STATUS.CONNECTED) {
      status = CALL_STATUS.DISCONNECTED;
    } else {
      switch (reason) {
        case "Rejected":
          status = CALL_STATUS.DECLINED;
          break;
        case "Busy":
          status = CALL_STATUS.BUSY;
          break;
        case "VoiceMail":
          status = CALL_STATUS.NO_ANSWER;
          break;
        case "Disconnected":
        case "Gone":
        case "Parked":
          status = CALL_STATUS.DISCONNECTED;
          break;
        default:
          status = CALL_STATUS.FAILED;
      }
    }
    this.setStatus(status);
    stop?.();
    this._stopAllTimers();
  }

  // ✅ Call log se status detect karo
  async _pollCallLog() {
    const token = localStorage.getItem("ringCentral_access_token");
    if (!token) return;

    let connectedDetected = false;
    let callStartTime = Date.now();

    // const logInterval = setInterval(async () => {
    try {
      const res = await fetch(
        "https://platform.ringcentral.com/restapi/v1.0/account/~/extension/~/call-log?perPage=1&view=Detailed",
        { headers: { Authorization: `Bearer ${token}` } },
      );
      console.log("fetch status:", res);
      const data = await res.json();
      const record = data?.records?.[0];
      // Call connected
      if (record?.result === "Call connected" && !connectedDetected) {
        // Sirf recent call check karo (last 5 minutes)
        const recordTime = new Date(record?.startTime).getTime();
        const timeDiff = Date.now() - recordTime;
      }

      // ✅ Call end detect - disposed state
      // if (!this.currentSession) {
      //   clearInterval(logInterval);
      //   this._logInterval = null;
      // }
    } catch (e) {
      console.error("[RC CALL LOG] error:", e);
    }
    // }, 2000); // Har 2 second pe check

    // this._logInterval = logInterval;
  }

  // =====================
  // INBOUND CALLS
  // =====================

  // Wire the SDK's incoming-call event once. The WebPhone must be init()'d and
  // registered for this to fire, so init() is triggered on app load (not just
  // on first dial). Emits an "incoming" event the UI listens for.
  _attachInboundHandler() {
    if (!this.webPhone || this._inboundHandler) return;

    this._inboundHandler = (session) => {
      // Drop any leftover-but-dead session refs first, so a stale reference from
      // a previous call doesn't make us wrongly decline this new incoming call.
      this._clearDeadSessions();

      // Genuinely busy (live call or another incoming pending) -> politely decline.
      if (this.currentSession || this.incomingSession) {
        this._declineSession(session);
        return;
      }

      this.incomingSession = session;
      window.__rcIncoming = session;

      const callerId = this._extractCaller(session);

      // If the caller hangs up before we answer, clear the incoming UI.
      if (this._incomingDisposeInterval)
        clearInterval(this._incomingDisposeInterval);
      this._incomingDisposeInterval = setInterval(() => {
        const st = session?.state;
        if (st === "disposed" || st === "failed" || st === "terminated") {
          clearInterval(this._incomingDisposeInterval);
          this._incomingDisposeInterval = null;
          if (this.incomingSession === session) {
            this.incomingSession = null;
            this.emit({ type: "incoming-cancelled" });
          }
        }
      }, 300);

      this.emit({ type: "incoming", session, callerId });
    };

    this.webPhone.on("inboundCall", this._inboundHandler);
  }

  // Best-effort caller-number extraction across SDK object shapes. The WebPhone
  // v2 session may expose the caller as `remoteNumber`, or only inside the SIP
  // INVITE's From header (e.g. '"Name" <sip:+12145551234@host>;tag=...'). We log
  // the session so unknown shapes can be inspected via the console.
  _extractCaller(session) {
    const sm = session?.sipMessage;
    try {
      // eslint-disable-next-line no-console
      console.log("[RC inbound] session:", session, "sipMessage.headers:", sm?.headers);
    } catch (e) {
      /* ignore */
    }

    // The caller is the SIP "From" header on the inbound INVITE. Try direct
    // getters first, then the From header (under a few possible shapes). Do NOT
    // scan the whole message — the Request-URI / To header carry OUR own number.
    const fromHeader =
      sm?.headers?.From ??
      sm?.headers?.from ??
      session?.inviteRequest?.headers?.From ??
      session?.request?.headers?.From;

    const candidates = [
      session?.remoteNumber,
      session?.fromNumber,
      session?.from,
      session?.callInfo?.from,
      session?.remoteIdentity?.uri?.user,
      fromHeader,
    ].filter(Boolean);

    for (const c of candidates) {
      const str = String(c);
      // From header looks like: '"Caller" <sip:+12145551234@host>;tag=...'.
      // Prefer the user part of the sip:/tel: URI; else fall back to the raw
      // value (a bare number), trimmed of any host after '@'.
      const uri = str.match(/(?:sip|tel):(\+?\d{6,})/i);
      const base = uri ? uri[1] : str.split("@")[0];
      const digits = base.replace(/[^\d]/g, "");
      if (digits.length >= 6) return `+${digits}`;
    }
    return "Unknown";
  }

  // Answer the pending incoming call.
  async acceptIncoming() {
    const session = this.incomingSession;
    if (!session) return null;
    this.incomingSession = null;
    if (this._incomingDisposeInterval) {
      clearInterval(this._incomingDisposeInterval);
      this._incomingDisposeInterval = null;
    }

    this.currentSession = session;
    window.__rcSession = session;

    try {
      if (typeof session.answer === "function") await session.answer();
      else if (typeof session.accept === "function") await session.accept();
      else throw new Error("Incoming session has no answer()/accept()");
    } catch (err) {
      this.lastError = err?.message;
      this.currentSession = null;
      this.setStatus(CALL_STATUS.FAILED);
      throw err;
    }

    this.setStatus(CALL_STATUS.CONNECTED);
    this._watchSessionDispose(session);
    return session;
  }

  // Reject/decline the pending incoming call.
  async rejectIncoming() {
    const session = this.incomingSession;
    this.incomingSession = null;
    if (this._incomingDisposeInterval) {
      clearInterval(this._incomingDisposeInterval);
      this._incomingDisposeInterval = null;
    }
    if (session) {
      await this._declineSession(session);
      // Let the UI log it as a missed/declined inbound call.
      this.emit({ type: "incoming-cancelled" });
    }
  }

  async _declineSession(session) {
    try {
      if (typeof session.decline === "function") await session.decline();
      else if (typeof session.reject === "function") await session.reject();
      else if (typeof session.toVoicemail === "function")
        await session.toVoicemail();
      else if (typeof session.hangup === "function") await session.hangup();
    } catch (e) {
      /* ignore — call may already be gone */
    }
  }

  // For an answered inbound call: end the UI when either side hangs up.
  _watchSessionDispose(session) {
    this._stopAllTimers();
    this._disposeInterval = setInterval(() => {
      const st = session?.state;
      if (st === "disposed" || st === "failed" || st === "terminated") {
        this._markEnd("remote", `sip session ${st}`);
        clearInterval(this._disposeInterval);
        this._disposeInterval = null;
        this._stopAllTimers();
        this.currentSession = null;
        this.setStatus(CALL_STATUS.DISCONNECTED);
      }
    }, 300);
  }

  // =====================
  // STOP ALL TIMERS
  // =====================
  _stopAllTimers() {
    // Stop reading stats, but keep the monitor: the summary is emitted when
    // the terminal status arrives (setStatus) so the row carries how it ended.
    this._stopQualitySampling();
    if (this._stateInterval) {
      clearInterval(this._stateInterval);
      this._stateInterval = null;
    }
    if (this._disposeInterval) {
      clearInterval(this._disposeInterval);
      this._disposeInterval = null;
    }
    if (this._connectedTimer) {
      clearTimeout(this._connectedTimer);
      this._connectedTimer = null;
    }
    if (this._telephonyInterval) {
      // Now a self-scheduling setTimeout chain (see _startTelephonyStatusPolling).
      clearTimeout(this._telephonyInterval);
      this._telephonyInterval = null;
    }
    if (this._webhookGraceTimer) {
      clearTimeout(this._webhookGraceTimer);
      this._webhookGraceTimer = null;
    }
  }

  // =====================
  // DTMF
  // =====================
  sendDTMF(digit) {
    try {
      if (!this.currentSession) {
        console.warn("[DTMF] No session!");
        return;
      }
      if (typeof this.currentSession.sendDtmf === "function") {
        this.currentSession.sendDtmf(digit);
        console.log("[DTMF] sent:", digit);
      } else {
        console.error("[DTMF] sendDtmf not found!");
      }
    } catch (e) {
      console.error("[DTMF] error:", e);
    }
  }

  async sendDTMFSequence(sequence, delay = 600) {
    if (!sequence) return;
    for (let i = 0; i < sequence.length; i++) {
      this.sendDTMF(sequence[i]);
      await new Promise((r) => setTimeout(r, delay));
    }
  }

  // =====================
  // ACTIONS
  // =====================
  async hangup() {
    // Mark BEFORE tearing down: the dispose watcher / webhook that follow
    // would otherwise claim "remote".
    if (this.currentSession) this._markEnd("agent");
    this._stopAllTimers();
    this._stopSilenceInterval();
    if (!this.currentSession) return;
    try {
      await this.currentSession.hangup?.();
    } catch {}
    this.currentSession = null;
    this.setStatus(CALL_STATUS.DISCONNECTED);
  }

  async mute(muted) {
    if (!this.currentSession) return;
    try {
      muted ? this.currentSession.mute?.() : this.currentSession.unmute?.();
    } catch {}
  }

  async hold(held) {
    if (!this.currentSession) return;
    try {
      if (held) {
        await this.currentSession.hold?.();
        this.setStatus(CALL_STATUS.HOLD);
      } else {
        await this.currentSession.unhold?.();
        this.setStatus(CALL_STATUS.CONNECTED);
      }
    } catch {}
  }
}

const ringCentralWebPhoneService = new RingCentralWebPhoneService();
window.__rcService = ringCentralWebPhoneService;
export default ringCentralWebPhoneService;
